import { Box, Button, CircularProgress, ListItemText, Paper, Popper, SxProps, Theme } from '@mui/material';
import MuiAutocomplete, {
  AutocompleteChangeReason,
  AutocompleteInputChangeReason,
  AutocompleteRenderInputParams
} from '@mui/material/Autocomplete';
import React, { ReactNode } from 'react';

import TextField from '../Textfield';
import { RGBA_COLORS } from '@/common/presentation/constants/colors';
import tid from '@/common/infrastructure/config/tid';

export type AutocompleteAction = {
  /** Label for the action */
  label: string;
  /** Icon to display next to the action */
  icon?: ReactNode;
  /** Callback fired when the action is clicked */
  onClick: () => void;
};

export type OptionType = {
  value: string | number;
  label: string;
};

export type AutocompleteChangeDetails = {
  option: unknown;
};

export type CustomAutocompleteProps<T extends object | string = string> = {
  /** ID of the input */
  id?: string;
  /** Helper text displayed below the input */
  helperText?: ReactNode;
  /** Label for the input field */
  label?: string;
  /** Array of options to display in the dropdown */
  options: T[];
  /** Whether the input is in an error state */
  error?: boolean;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Whether the input should take up the full width of its container */
  fullWidth?: boolean;
  /** Whether multiple values can be selected */
  multiple?: boolean;
  /** Current value of the input */
  value?: T | T[] | null;
  /** Callback fired when the value changes */
  onChange?: (
    event: React.SyntheticEvent,
    newValue: T | T[] | null,
    reason?: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails
  ) => void;
  /** Callback fired when the input value changes */
  onInputChange?: (event: React.SyntheticEvent, newInputValue: string, reason?: AutocompleteInputChangeReason) => void;
  /** The input value of the autocomplete */
  inputValue?: string;
  /** Callback fired when the input loses focus */
  onBlur?: React.FocusEventHandler<HTMLDivElement>;
  /** Whether to clear the input on blur */
  clearOnBlur?: boolean;
  /** Whether to clear the input on escape */
  clearOnEscape?: boolean;
  /** Whether to disable the clear button */
  disableClearable?: boolean;
  /** Whether to disable closing on select */
  disableCloseOnSelect?: boolean;
  /** Whether to disable the portal */
  disablePortal?: boolean;
  /** Function to filter options */
  filterOptions?: (options: T[], state: { inputValue: string }) => T[];
  /** Function to get the label for an option */
  getOptionLabel?: (option: T) => string;
  /** Function to check if an option is equal to a value */
  isOptionEqualToValue?: (option: T, value: T) => boolean;
  /** Function to get a unique key for an option */
  getOptionKey?: (option: T) => string | number;
  /** Whether the component is in a loading state */
  loading?: boolean;
  /** Whether the component is in a loading  for the options */
  optionsloading?: boolean;
  /** Text to display when loading */
  loadingText?: ReactNode;
  /** Text to display when no options are available */
  noOptionsText?: ReactNode;
  /** Whether the dropdown is open */
  open?: boolean;
  /** Callback fired when the dropdown opens */
  onOpen?: () => void;
  /** Callback fired when the dropdown closes */
  onClose?: () => void;
  /** Function to render an option */
  renderOption?: (props: React.HTMLAttributes<HTMLLIElement>, option: T) => React.ReactNode;
  /** Function to render tags */
  renderTags?: (
    value: T[],
    getTagProps: (params: { index: number }) => {
      key: number | string;
      'data-tag-index': number;
      tabIndex: number;
      onDelete: (event: React.SyntheticEvent) => void;
    }
  ) => React.ReactNode;
  /** Size of the input */
  size?: 'small' | 'medium';
  /** Styles to apply to the component */
  sx?: SxProps<Theme>;
  /** Custom actions to display at the top of the dropdown */
  actions?: AutocompleteAction[];
  /** Custom input renderer for the autocomplete input */
  renderInput?: (params: AutocompleteRenderInputParams) => React.ReactNode;
  /** Whether to focus the input on mount */
  autoFocus?: boolean;
  /** Reference to the input element */
  inputRef?: React.RefObject<HTMLInputElement | null>;
  /** Styles to apply to the text field */
  textFieldSx?: SxProps;
  /** End adornment to display at the end of the input */
  endAdornment?: ReactNode;
  /** Start adornment to display at the start of the input */
  startAdornment?: ReactNode;
  /** Placeholder text to display in the input */
  placeholder?: string;
  /** Test ID for testing purposes */
  'data-testid'?: string;
  /** Props to be passed to the internal MUI components */
  slotProps?: Record<string, object>;
  required?: boolean;
};

/**
 * Enhanced Autocomplete component that wraps MUI's Autocomplete
 *
 * @example
 * ```tsx
 * <Autocomplete
 *   label="Select a country"
 *   options={['USA', 'Canada', 'Mexico']}
 *   onChange={(_, value) => console.log(value)}
 *   actions={[
 *     {
 *       label: 'View All',
 *       icon: <ListIcon />,
 *       onClick: () => navigate('/countries')
 *     },
 *     {
 *       label: 'Create New',
 *       icon: <AddIcon />,
 *       onClick: () => navigate('/countries/new')
 *     }
 *   ]}
 * />
 * ```
 */
const Autocomplete = <T extends object | string>({
  id,
  options,
  label,
  helperText,
  error = false,
  disabled = false,
  fullWidth = false,
  multiple = false,
  value,
  onChange,
  onInputChange,
  inputValue,
  onBlur,
  clearOnBlur = true,
  clearOnEscape = true,
  disableClearable = false,
  disableCloseOnSelect = false,
  disablePortal = false,
  filterOptions,
  getOptionLabel,
  isOptionEqualToValue,
  getOptionKey,
  loading = false,
  optionsloading = false,
  loadingText = 'Loading...',
  noOptionsText = 'No options',
  open,
  onOpen,
  onClose,
  renderOption,
  renderTags,
  size = 'medium',
  sx,
  actions,
  renderInput,
  autoFocus = false,
  inputRef,
  textFieldSx,
  endAdornment,
  startAdornment,
  placeholder,
  'data-testid': dataTestId,
  slotProps,
  required,
  ...rest
}: CustomAutocompleteProps<T>) => {
  const { 'data-testid': _, ...restWithoutTestId } = rest as Record<string, unknown> & { 'data-testid'?: string };
  const renderCustomOption = (props: React.HTMLAttributes<HTMLLIElement> & { key?: React.Key }, option: T) => {
    if (renderOption) {
      return renderOption(props, option);
    }

    const { key, ...otherProps } = props;
    const optionKey = getOptionKey ? getOptionKey(option) : ((option as { key?: string | number })?.key ?? key);
    return (
      <li key={optionKey} {...otherProps}>
        <ListItemText primary={getOptionLabel ? getOptionLabel(option) : String(option)} />
      </li>
    );
  };

  // Custom Popper component to render actions
  const CustomPopper = (props: React.ComponentProps<typeof Popper>) => {
    const children = typeof props.children === 'function' ? props.children({} as never) : props.children;
    return (
      <Popper {...props} placement="bottom">
        <Paper
          elevation={3}
          sx={{
            borderRadius: '4px',
            overflow: 'hidden'
          }}
        >
          {actions && actions.length > 0 && (
            <Box
              sx={{
                p: 0,
                borderBottom: `1px solid ${RGBA_COLORS.GRAY_12}`,
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: 'background.paper',
                '& .MuiButton-root': {
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                  borderRadius: 0,
                  padding: '8px 16px',
                  minHeight: '48px',
                  '&:hover': {
                    backgroundColor: 'action.hover'
                  }
                }
              }}
            >
              {actions.map((action, index) => (
                <Button
                  key={index}
                  size="small"
                  startIcon={action.icon}
                  onClick={(e) => {
                    e.stopPropagation();
                    action.onClick();
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                  }}
                  fullWidth
                  {...tid(`common:autocomplete:action-button-${index}`)}
                >
                  {action.label}
                </Button>
              ))}
            </Box>
          )}
          {children}
        </Paper>
      </Popper>
    );
  };

  const defaultRenderInput = (params: AutocompleteRenderInputParams) => {
    const textFieldConfig = slotProps?.textField as
      | { slotProps?: Record<string, unknown>; endAdornmentTestId?: Record<string, string> }
      | undefined;
    const textFieldSlotProps = textFieldConfig?.slotProps;
    const endAdornmentTestId = textFieldConfig?.endAdornmentTestId;
    const endAdornmentContent = endAdornment || (
      <>
        {optionsloading && <CircularProgress color="inherit" size={20} />}
        {params.InputProps.endAdornment}
      </>
    );

    // For multiple-select autocompletes, MUI passes the selected chips as
    // params.InputProps.startAdornment. Merge with any custom startAdornment
    // instead of overwriting, otherwise chips are silently erased.
    const mergedStartAdornment = startAdornment ? (
      <>
        {params.InputProps.startAdornment}
        {startAdornment}
      </>
    ) : (
      params.InputProps.startAdornment
    );

    const mergedInputProps = {
      ...params.InputProps,
      startAdornment: mergedStartAdornment,
      endAdornment: endAdornmentTestId ? <span {...endAdornmentTestId}>{endAdornmentContent}</span> : endAdornmentContent
    };

    const htmlInputProps = (textFieldSlotProps?.htmlInput ?? {}) as Record<string, unknown>;
    const mergedInputHTMLProps = {
      ...params.inputProps,
      ...htmlInputProps
    };

    const safeSlotProps = textFieldSlotProps
      ? Object.keys(textFieldSlotProps)
          .filter((key) => key !== 'htmlInput' && key !== 'input')
          .reduce(
            (acc, key) => {
              acc[key] = textFieldSlotProps[key];
              return acc;
            },
            {} as Record<string, unknown>
          )
      : undefined;

    return (
      <TextField
        id={id}
        inputProps={mergedInputHTMLProps}
        InputProps={mergedInputProps}
        slotProps={safeSlotProps && Object.keys(safeSlotProps).length > 0 ? safeSlotProps : undefined}
        placeholder={placeholder}
        label={label}
        name={id}
        error={error}
        helperText={helperText}
        fullWidth={fullWidth}
        size={size}
        autoFocus={autoFocus}
        inputRef={inputRef}
        sx={textFieldSx}
        required={required}
      />
    );
  };

  const renderInputWithTestId = (params: AutocompleteRenderInputParams) => {
    return (renderInput ?? defaultRenderInput)(params);
  };

  const popperSlots = actions && actions.length > 0 ? { popper: CustomPopper } : undefined;

  return (
    <MuiAutocomplete
      id={id}
      options={options}
      disabled={disabled}
      multiple={multiple}
      value={value}
      onChange={onChange}
      onInputChange={onInputChange}
      inputValue={inputValue}
      onBlur={onBlur}
      clearOnBlur={clearOnBlur}
      clearOnEscape={clearOnEscape}
      disableClearable={disableClearable}
      disableCloseOnSelect={disableCloseOnSelect}
      disablePortal={disablePortal}
      filterOptions={filterOptions}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      loading={loading}
      loadingText={loadingText}
      noOptionsText={noOptionsText}
      open={open}
      onOpen={onOpen}
      onClose={onClose}
      renderInput={renderInputWithTestId}
      renderOption={renderCustomOption}
      renderTags={renderTags}
      sx={sx}
      slots={popperSlots}
      slotProps={slotProps}
      {...restWithoutTestId}
    />
  );
};

export default Autocomplete;
